"use client";

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getServerUrl, getAuthHeaders } from "../../Utils/serverSelector";
import { resetStore } from "./resetStore";

/* ============== HELPERS ============== */

const authJsonConfig = async () => {
    const headers = await getAuthHeaders();

    return {
        headers,
        withCredentials: true,
        validateStatus: () => true,
    };
};

// base64 VAPID-ключ → Uint8Array
const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);

    const base64 = (base64String + padding)
        .replace(/-/g, "+")
        .replace(/_/g, "/");

    const rawData = window.atob(base64);

    return Uint8Array.from(
        [...rawData].map((char) => char.charCodeAt(0))
    );
};

const isWebPushSupported = () =>
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

const getWebPushDeviceId = () => {
    const storageKey = "zmanup_web_push_device_id";

    let deviceId = localStorage.getItem(storageKey);

    if (!deviceId) {
        deviceId =
            window.crypto?.randomUUID?.() ||
            `${Date.now()}-${Math.random().toString(36).slice(2)}`;

        localStorage.setItem(storageKey, deviceId);
    }

    return deviceId;
};

/* ============== THUNKS ============== */

// Разрешение → Service Worker → подписка → сохранение на сервере
export const subscribeWebPush = createAsyncThunk(
    "webPush/subscribe",
    async (uid, { rejectWithValue }) => {
        try {
            if (!uid) {
                throw new Error("UID не передан");
            }

            if (!isWebPushSupported()) {
                throw new Error(
                    "Web Push не поддерживается этим браузером"
                );
            }

            let permission = Notification.permission;

            if (permission === "default") {
                permission = await Notification.requestPermission();
            }

            if (permission !== "granted") {
                throw new Error(
                    `Уведомления не разрешены: ${permission}`
                );
            }

            const registration =
                await navigator.serviceWorker.register("/sw-push.js");

            await navigator.serviceWorker.ready;

            const base = String(getServerUrl() || "").replace(/\/$/, "");

            const keyResponse = await axios.get(
                `${base}/web-push/vapid-public-key`,
                {
                    withCredentials: true,
                    validateStatus: () => true,
                }
            );

            if (
                keyResponse.status !== 200 ||
                !keyResponse.data?.publicKey
            ) {
                throw new Error(
                    keyResponse.data?.error ||
                    "VAPID ключ не получен"
                );
            }

            let subscription =
                await registration.pushManager.getSubscription();

            if (!subscription) {
                subscription =
                    await registration.pushManager.subscribe({
                        userVisibleOnly: true,
                        applicationServerKey:
                            urlBase64ToUint8Array(
                                keyResponse.data.publicKey
                            ),
                    });
            }

            const deviceId = getWebPushDeviceId();

            const cfg = await authJsonConfig();

            const { status, data } = await axios.post(
                `${base}/web-push/subscribe`,
                {
                    uid: String(uid),
                    deviceId,
                    subscription: subscription.toJSON(),
                    userAgent: navigator.userAgent,
                },
                cfg
            );

            if (status < 200 || status >= 300) {
                throw new Error(
                    data?.error ||
                    `HTTP ${status}`
                );
            }

            return {
                endpoint: subscription.endpoint,
                deviceId,
            };
        } catch (error) {

            return rejectWithValue(
                error?.response?.data?.error ||
                error?.response?.data ||
                error?.message ||
                "subscribe failed"
            );
        }
    }
);

// Вызывать только при ручном отключении уведомлений.
// Не вызывать при обычном logout.
export const unsubscribeWebPush = createAsyncThunk(
    "webPush/unsubscribe",
    async (_, { rejectWithValue }) => {
        try {
            if (!isWebPushSupported()) {
                return null;
            }

            const registration =
                await navigator.serviceWorker.getRegistration("/sw-push.js");

            const subscription =
                await registration?.pushManager?.getSubscription();

            if (!subscription) {
                return null;
            }

            const endpoint = subscription.endpoint;
            const deviceId = getWebPushDeviceId();
            const base = String(getServerUrl() || "").replace(/\/$/, "");
            const cfg = await authJsonConfig();

            const { status, data } = await axios.post(
                `${base}/web-push/unsubscribe`,
                {
                    endpoint,
                    deviceId,
                },
                cfg
            );

            if (status < 200 || status >= 300) {
                throw new Error(
                    data?.error ||
                    `HTTP ${status}`
                );
            }

            await subscription.unsubscribe();

            return {
                endpoint,
                deviceId,
            };
        } catch (error) {
            return rejectWithValue(
                error?.response?.data?.error ||
                error?.response?.data ||
                error?.message ||
                "unsubscribe failed"
            );
        }
    }
);

/* ============== STATE ============== */

const initialState = {
    endpoint: null,
    deviceId: null,
    isSubscribed: false,
    status: "idle",
    error: null,
};

/* ============== SLICE ============== */

const webPushSlice = createSlice({
    name: "webPush",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(subscribeWebPush.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })

            .addCase(subscribeWebPush.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.isSubscribed = true;
                state.endpoint = action.payload?.endpoint || null;
                state.deviceId = action.payload?.deviceId || null;
            })

            .addCase(subscribeWebPush.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload;
                state.isSubscribed = false;
            })

            .addCase(unsubscribeWebPush.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })

            .addCase(unsubscribeWebPush.fulfilled, (state) => {
                state.isSubscribed = false;
                state.endpoint = null;
                state.deviceId = null;
                state.status = "idle";
                state.error = null;
            })

            .addCase(unsubscribeWebPush.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload;
            })

            .addCase(resetStore, () => initialState);
    },
});

export default webPushSlice.reducer;
